import {
  SupportChannel,
  SupportDismissMethod,
  SupportSurface,
  SupportTrigger
} from 'Design-system/app/static/scripts/support-widget';
import { BusinessModel, PostingFlows } from 'Douleutaras/types';

export interface ProCardViewedProperties {
  category_slug: string;
  sub_category_slug: string;
  municipality_slug: string;
  county_slug: string;
  positions?: Array<string>;
  pro_ids?: Array<string>;
}

export interface PhoneNumberRevealedProperties {
  category_slug: string;
  sub_category_slug?: string;
  business_unit_slug: string;
  business_model: BusinessModel;
  municipality_slug?: string;
  county_slug?: string;
  job_posting_flow: PostingFlows;
  is_cleaning: boolean;
  is_b2b: boolean;
  is_core: boolean;
  job_id: string;
  bid_id: string;
  pro_id: string;
  customer_id: string;
  source_phone_reveal: PhoneNumberRevealedSource;
}

export interface SuccessModalShownProperties {
  category_slug: string;
  sub_category_slug: string;
  business_unit_slug: string;
  municipality_slug: string;
  county_slug: string;
  is_b2b: boolean;
  is_cleaning: boolean;
  is_core: boolean;
  job_id: number;
  app_source?: string;
  platform: string;
  pro_ids_matched: Array<string>;
  top_pros_matched_count: number;
  new_pros_matched_count: number;
}

export enum SuccessModalAction {
  PROFILE_VISIT = 'profile_visit',
  CHAT = 'chat',
  PHONE = 'phone',
  EXTRA_PRO_FLOW_TRIGGERED = 'extra_pro_flow_triggered',
  CLOSED = 'closed',
  SCROLL = 'scroll'
}

export interface SuccessModalActionProperties extends SuccessModalShownProperties {
  action: SuccessModalAction;
}

export enum PhoneNumberRevealedSource {
  BID_CARD = 'bid_card',
  CHAT = 'chat',
  NEED_HELP = 'need_help',
  EXPRESS_YOUR_INTEREST = 'express_your_interest',
  SUCCESS_MODAL = 'success_modal'
}

export enum ServicesSectionItem {
  CATEGORY_EXPAND = 'category_expand',
  SERVICE = 'service',
  DETAIL = 'detail',
  CATEGORY_INFO = 'category_info',
  SERVICE_INFO = 'service_info',
  DETAIL_INFO = 'detail_info'
}

// Nullable props are `T | null`, not `?`: PostHog filters "not set" separately from "null".
// `price_type`/`price_unit` stay `string` so this module needs no view imports; the values are
// constrained at the producer, which reads them off a typed `ServicePrice`.
export interface ServicesSectionClickedProperties {
  item_clicked: ServicesSectionItem;
  is_form_mapped: boolean | null;
  category_id: string;
  category_title: string;
  service_id: string | null;
  service_title: string | null;
  detail_id: string | null;
  detail_title: string | null;
  price_min: number | null;
  price_max: number | null;
  price_type: string | null;
  price_unit: string | null;
}

// -----------------------------------------------------------------------------
// Customer support (#5619)
//
// One property vocabulary shared by every support surface — the values come from the
// design-system, the property bags are below. The dictionary tab is the source of truth
// for required-ness:
// https://docs.google.com/spreadsheets/d/1C-8li3qiRupAi4QAUB8JhPU9TLV8dDzyxNpXnDMWjYA
// -----------------------------------------------------------------------------

// The vocabulary itself lives in the design-system so the Django adapter reports the
// same values — a `surface` that differs by a character is two funnels, silently.
export { SupportChannel, SupportDismissMethod, SupportSurface, SupportTrigger };

export interface SupportEventProperties {
  surface: SupportSurface;
  trigger: SupportTrigger;
  support_available: boolean;
  category_slug?: string;
  job_id?: string;
  customer_id?: string;
}

export interface SupportChannelSelectedProperties extends SupportEventProperties {
  channel: SupportChannel;
}

export interface SupportWidgetDismissedProperties extends SupportEventProperties {
  dismiss_method: SupportDismissMethod;
  seconds_open?: number;
}
